"use client";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Progress } from "../../components/ui/progress";
import { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { selectAuthState } from "../../../store/selectors/authSelectors";
import { connect } from "react-redux";
import FlowModalFMM from "../FlowManagement/components/FlowModalFMM";
import { AlertTriangle } from "lucide-react";
import { cn } from "../../../utils/tw";

// Modified budget data with sub-elements
const budgetData = [
  {
    category: "Development",
    spent: 450000,
    budget: 500000,
    color: "#2C5282", // Deep professional blue (was #FFD700)
    subElements: [
      {
        category: "Frontend",
        spent: 200000,
        budget: 220000,
        color: "#4299E1", // Muted blue (was #FFE44D)
        iprs: [
          {
            id: "IPR-001",
            title: "React Component Library License",
            origin_channel: "Email",
            status: "Approved",
            ai_summary:
              "Request for annual license renewal of component library",
            criticality: "high",
            delivery_target_date: "2024-03-15",
            area: "Frontend",
            created_by_name: "John Doe",
            justification: "Required for ongoing development",
            source_email: "john@company.com",
            step: "Implementation",
            budget: 5000,
            amount_spent: 5000,
          },
          {
            id: "IPR-002",
            title: "Design System Tools",
            origin_channel: "Portal",
            status: "Pending",
            ai_summary: "New design system implementation tools",
            criticality: "medium",
            delivery_target_date: "2024-04-01",
            area: "Frontend",
            created_by_name: "Jane Smith",
            justification: "Improve design consistency",
            source_email: "jane@company.com",
            step: "Review",
            budget: 3000,
            amount_spent: 0,
          },
        ],
      },
      {
        category: "Backend",
        spent: 180000,
        budget: 200000,
        color: "#2B6CB0", // Darker blue (was #FFD700)
      },
      {
        category: "QA",
        spent: 70000,
        budget: 80000,
        color: "#63B3ED", // Light blue (was #FFE44D)
      },
    ],
  },
  {
    category: "Design",
    spent: 180000,
    budget: 200000,
    color: "#805AD5", // Deep purple (was #FFA500)
    subElements: [
      {
        category: "UI Design",
        spent: 100000,
        budget: 120000,
        color: "#9F7AEA", // Muted purple (was #FFB84D)
      },
      {
        category: "UX Research",
        spent: 50000,
        budget: 60000,
        color: "#6B46C1", // Dark purple (was #FFA500)
      },
      {
        category: "Branding",
        spent: 30000,
        budget: 20000,
        color: "#B794F4", // Light purple (was #FFB84D)
      },
    ],
  },
  {
    category: "Marketing",
    spent: 120000,
    budget: 150000,
    color: "#C53030", // Deep red (was #FF6347)
    subElements: [
      {
        category: "Digital Ads",
        spent: 60000,
        budget: 75000,
        color: "#E53E3E", // Muted red (was #FF7A5C)
      },
      {
        category: "Content",
        spent: 40000,
        budget: 50000,
        color: "#9B2C2C", // Dark red (was #FF6347)
      },
      {
        category: "Events",
        spent: 20000,
        budget: 25000,
        color: "#FC8181", // Light red (was #FF7A5C)
      },
    ],
  },
  {
    category: "Infrastructure",
    spent: 80000,
    budget: 100000,
    color: "#4A5568", // Deep gray (was #9370DB)
    subElements: [
      {
        category: "Cloud Services",
        spent: 50000,
        budget: 60000,
        color: "#718096", // Muted gray (was #A78BFA)
      },
      {
        category: "Hardware",
        spent: 30000,
        budget: 40000,
        color: "#2D3748", // Dark gray (was #9370DB)
      },
    ],
  },
  {
    category: "Research",
    spent: 50000,
    budget: 80000,
    color: "#276749", // Deep green (was #4169E1)
    subElements: [
      {
        category: "Market Research",
        spent: 30000,
        budget: 50000,
        color: "#38A169", // Muted green (was #60A5FA)
      },
      {
        category: "R&D",
        spent: 20000,
        budget: 30000,
        color: "#22543D", // Dark green (was #4169E1)
      },
    ],
  },
  {
    category: "Operations",
    spent: 1150000,
    budget: 1000000,
    color: "#2F855A",
    subElements: [
      {
        category: "Production and Manufacturing",
        spent: 450000,
        budget: 400000,
        color: "#48BB78",
        iprs: [
          {
            id: "130",
            title: "Cardboard Box Replenishment - Q3",
            origin_channel: "Email",
            status: "Approved",
            ai_summary: "Request for cardboard boxes for Q3 product packaging",
            criticality: "medium",
            area: "Production and Manufacturing",
            created_by_name: "Ana Maria Rodriguez",
            justification:
              "Cardboard box inventory is low and required for upcoming product batch.",
            source_email: "ana.rodriguez@company.com",
            step: "PO Creation",
            budget: 60000,
            amount_spent: 70000,
            created_at: "2025-05-25",
            delivery_target_date: "2025-06-15",
            validations: [
              {
                title: "Inventario actual de cajas de cartón",
                fields: [
                  { label: "Fuente", value: "ERP – Módulo de Inventario" },
                  {
                    label: "",
                    value:
                      "Stock por debajo del mínimo, próxima a nivel crítico",
                  },
                  {
                    label: "",
                    value:
                      "Se requiere reposición urgente para evitar impacto en la producción de julio.",
                  },
                  { label: "Stock actual", value: "1.080 unidades" },
                  {
                    label: "Consumo mensual promedio",
                    value: "1.500 unidades",
                  },
                  {
                    label: "Nivel de stock mínimo establecido",
                    value: "1.200 unidades",
                  },
                  { label: "Nivel de stock crítico", value: "900 unidades" },
                ],
              },
              {
                title: "Políticas de compra aplicables",
                fields: [
                  {
                    label: "Fuente",
                    value: "Manual de Políticas de Abastecimiento Interno v4.2",
                  },
                  {
                    label: "",
                    value: "Cumple políticas de pluralidad y evaluación formal",
                  },
                  {
                    label: "Política aplicable",
                    value:
                      "Compras superiores a COP $5.000.000 requieren mínimo 3 cotizaciones y evaluación técnico-económica.",
                  },
                  {
                    label: "Monto estimado de compra",
                    value: "COP $6.150.000",
                  },
                  {
                    label: "Cotizaciones recibidas",
                    value:
                      "3 EcoBox Logística y Empaque S.A., CartoPack Colombia Ltda., Empaques Andinos S.A.S.",
                  },
                  {
                    label: "Evaluación documentada",
                    value: "Sí, registrada por Coordinación de Compras",
                  },
                ],
              },
              {
                title: "Disponibilidad presupuestal",
                fields: [
                  {
                    label: "Fuente",
                    value:
                      "Plan financiero Q2-Q3 / Centro de Costos: 345 – Empaque Producción",
                  },
                  {
                    label: "",
                    value: "Compra dentro del presupuesto del área y rubro",
                  },
                  {
                    label: "Presupuesto asignado anual",
                    value: "COP $50.000.000",
                  },
                  { label: "Ejecutado a la fecha", value: "COP $28.400.000" },
                  {
                    label: "Disponible antes de compra",
                    value: "COP $21.600.000",
                  },
                  { label: "Costo de esta compra", value: "COP $6.150.000" },
                  {
                    label: "Aprobación formal",
                    value:
                      "Confirmada por Valeria Torres (Planeación Financiera)",
                  },
                ],
              },
              {
                title: "Validación de seguridad de la información",
                fields: [
                  {
                    label: "Fuente",
                    value: "Checklist de Seguridad y Compliance de Proveedores",
                  },
                  {
                    label: "",
                    value:
                      "Exento de revisión por no aplicar a sistemas ni datos críticos",
                  },
                  {
                    label:
                      "¿Producto involucra procesamiento de datos sensibles?",
                    value: "No",
                  },
                  {
                    label: "¿Proveedor tiene acceso a infraestructura digital?",
                    value: "No",
                  },
                  {
                    label: "¿Requiere revisión de seguridad?",
                    value: "No",
                  },
                ],
              },
            ],
          },
          {
            id: "131",
            title: "Machine Oil Supply for Converting Units",
            origin_channel: "Email",
            status: "Approved",
            ai_summary:
              "Procurement of machine oil for converting unit maintenance",
            criticality: "medium",
            delivery_target_date: "2025-06-20",
            area: "Production and Manufacturing",
            created_by_name: "Hernando Pérez",
            justification:
              "Monthly stock of machine oil required to avoid unplanned downtime.",
            source_email: "hernando.perez@company.com",
            step: "PO Creation",
            budget: 25000,
            amount_spent: 30000,
          },
          {
            id: "132",
            title: "New Embossing Roller for Tissue Line",
            origin_channel: "Email",
            status: "Approved",
            ai_summary: "Order of embossing roller for tissue folding line",
            criticality: "high",
            delivery_target_date: "2025-06-25",
            area: "Production and Manufacturing",
            created_by_name: "José Maldonado",
            justification:
              "Roller wear detected; critical for maintaining fold quality.",
            source_email: "jose.maldonado@company.com",
            step: "PO Creation",
            budget: 40000,
            amount_spent: 45000,
          },
          {
            id: "133",
            title: "Industrial Cleaning Agent Refill",
            origin_channel: "Email",
            status: "Approved",
            ai_summary:
              "Request for cleaning agents for converting machine sanitation",
            criticality: "medium",
            delivery_target_date: "2025-06-10",
            area: "Production and Manufacturing",
            created_by_name: "Ana Maria Rodriguez",
            justification:
              "Regular cleaning cycle due next week; current stock insufficient.",
            source_email: "ana.rodriguez@company.com",
            step: "PO Creation",
            budget: 20000,
            amount_spent: 18000,
          },
          {
            id: "134",
            title: "Label Roll Procurement for Packaging",
            origin_channel: "Email",
            status: "Approved",
            ai_summary: "Purchase of label rolls for production packaging line",
            criticality: "low",
            delivery_target_date: "2025-06-30",
            area: "Production and Manufacturing",
            created_by_name: "Hernando Pérez",
            justification:
              "Label stock for retail packaging is below minimum threshold.",
            source_email: "hernando.perez@company.com",
            step: "PO Creation",
            budget: 15000,
            amount_spent: 12000,
          },
          {
            id: "135",
            title: "Gearbox Replacement - Converting Line",
            origin_channel: "Email",
            status: "Approved",
            ai_summary: "Request for replacement gearboxes for converting unit",
            criticality: "high",
            delivery_target_date: "2025-07-05",
            area: "Production and Manufacturing",
            created_by_name: "José Maldonado",
            justification:
              "Gearboxes showing signs of degradation under thermal stress.",
            source_email: "jose.maldonado@company.com",
            step: "PO Creation",
            budget: 55000,
            amount_spent: 60000,
          },
          {
            id: "136",
            title: "Plastic Wrap Film Supply - Bulk Packaging",
            origin_channel: "Email",
            status: "Approved",
            ai_summary: "Order of plastic wrap film for bulk packaging",
            criticality: "medium",
            delivery_target_date: "2025-06-12",
            area: "Production and Manufacturing",
            created_by_name: "Ana Maria Rodriguez",
            justification:
              "Increased production volumes require additional packaging material.",
            source_email: "ana.rodriguez@company.com",
            step: "PO Creation",
            budget: 35000,
            amount_spent: 40000,
          },
          {
            id: "137",
            title: "Rewinder Cutter Blade Replacements",
            origin_channel: "Email",
            status: "Approved",
            ai_summary:
              "Acquisition of spare cutter blades for rewinder machine",
            criticality: "high",
            delivery_target_date: "2025-06-08",
            area: "Production and Manufacturing",
            created_by_name: "Hernando Pérez",
            justification:
              "Blades reaching end of service life, risk of poor edge quality.",
            source_email: "hernando.perez@company.com",
            step: "PO Creation",
            budget: 50000,
            amount_spent: 55000,
          },
          {
            id: "138",
            title: "Lubricant Restock - Bearing Systems",
            origin_channel: "Email",
            status: "Approved",
            ai_summary:
              "Request for high-viscosity lubricants for bearing systems",
            criticality: "medium",
            delivery_target_date: "2025-06-18",
            area: "Production and Manufacturing",
            created_by_name: "José Maldonado",
            justification:
              "Lubricant stocks are depleting, required for next PM cycle.",
            source_email: "jose.maldonado@company.com",
            step: "PO Creation",
            budget: 45000,
            amount_spent: 50000,
          },
          {
            id: "139",
            title: "New Cutting Dies for Napkin Line",
            origin_channel: "Email",
            status: "Approved",
            ai_summary: "Purchase of cutting dies for napkin folding equipment",
            criticality: "high",
            delivery_target_date: "2025-06-22",
            area: "Production and Manufacturing",
            created_by_name: "Ana Maria Rodriguez",
            justification:
              "New SKUs require different folding patterns and dies.",
            source_email: "ana.rodriguez@company.com",
            step: "PO Creation",
            budget: 55000,
            amount_spent: 70000,
          },
        ],
      },
      {
        category: "Maintenance & Engineering",
        spent: 280000,
        budget: 250000,
        color: "#2F855A",
        iprs: [
          {
            id: "IPR-102",
            title: "Preventive Maintenance Equipment",
            origin_channel: "Email",
            status: "Approved",
            ai_summary:
              "New maintenance equipment for preventive maintenance program",
            criticality: "medium",
            delivery_target_date: "2024-05-01",
            area: "Maintenance",
            created_by_name: "Sarah Chen",
            justification: "Essential for equipment reliability",
            source_email: "sarah@company.com",
            step: "Implementation",
            budget: 75000,
            amount_spent: 25000,
          },
        ],
      },
      {
        category: "Quality Assurance / Laboratory",
        spent: 200000,
        budget: 180000,
        color: "#34D399",
        iprs: [
          {
            id: "IPR-103",
            title: "Lab Equipment Calibration",
            origin_channel: "Portal",
            status: "Approved",
            ai_summary: "Annual calibration of laboratory equipment",
            criticality: "high",
            delivery_target_date: "2024-04-15",
            area: "QA",
            created_by_name: "David Kim",
            justification: "Required for maintaining quality standards",
            source_email: "david@company.com",
            step: "Implementation",
            budget: 45000,
            amount_spent: 45000,
          },
        ],
      },
      {
        category: "Logistics and Warehouse",
        spent: 140000,
        budget: 120000,
        color: "#10B981",
        iprs: [
          {
            id: "IPR-104",
            title: "Warehouse Management System",
            origin_channel: "Email",
            status: "Pending",
            ai_summary: "Implementation of new warehouse management system",
            criticality: "medium",
            delivery_target_date: "2024-07-01",
            area: "Logistics",
            created_by_name: "Lisa Wong",
            justification: "Improve inventory management efficiency",
            source_email: "lisa@company.com",
            step: "Review",
            budget: 80000,
            amount_spent: 0,
          },
        ],
      },
      {
        category: "Cleaning & Sanitation",
        spent: 80000,
        budget: 50000,
        color: "#059669",
        iprs: [
          {
            id: "IPR-105",
            title: "Sanitation Equipment Upgrade",
            origin_channel: "Portal",
            status: "Approved",
            ai_summary: "Upgrade of cleaning and sanitation equipment",
            criticality: "medium",
            delivery_target_date: "2024-05-15",
            area: "Sanitation",
            created_by_name: "Robert Martinez",
            justification: "Maintain hygiene standards",
            source_email: "robert@company.com",
            step: "Implementation",
            budget: 30000,
            amount_spent: 30000,
          },
        ],
      },
    ],
  },
];

// Calculate total budget and spent
const totalBudget = budgetData.reduce((sum, item) => sum + item.budget, 0);
const totalSpent = budgetData.reduce((sum, item) => sum + item.spent, 0);
const percentSpent = Math.round((totalSpent / totalBudget) * 100);

// Add this helper function before the BudgetDashboard component
const isOverBudget = (spent, budget) => spent > budget;

function BudgetDashboard({ mantisBusiness, user }) {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedData, setSelectedData] = useState(null);

  const formatColumnName = (columnName) => {
    // Convertir snake_case a palabras separadas
    const words = columnName.split("_");

    // Capitalizar cada palabra y unirlas con espacios
    return words
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };
  //Format value
  const formatCellValue = (value) => {
    //If value contains only numbers, add commas
    if (/^\d+$/.test(value)) {
      return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    } else {
      return value;
    }
  };
  const allColumns = [
    "id",
    "title",
    "status",
    "criticality",
    "delivery_target_date",
    "budget",
    "amount_spent",
    "created_by_name",
    "source_email",
    "step",
  ];
  const columnLabels = {};
  allColumns.forEach((column) => {
    columnLabels[column] = formatColumnName(column);
  });

  const renderBudgetGraph = (data, isSubElement = false) => {
    const totalBudget = data.reduce((sum, item) => sum + item.budget, 0);
    const totalSpent = data.reduce((sum, item) => sum + item.spent, 0);
    const percentSpent = Math.round((totalSpent / totalBudget) * 100);

    return (
      <div className="mb-8">
        <div className="flex h-8 w-full overflow-hidden rounded mb-2">
          {data.map((item) => {
            const widthPercentage = (item.budget / totalBudget) * 100;
            return (
              <div
                key={item.category}
                className="h-full relative group cursor-pointer"
                style={{
                  width: `${widthPercentage}%`,
                  backgroundColor: item.color,
                }}
                onClick={() => {
                  if (isSubElement) {
                    setSelectedSubCategory(item);
                  } else if (item.subElements) {
                    setSelectedCategory(item);
                    setSelectedSubCategory(null);
                  }
                }}
              >
                <div className="absolute inset-0 bg-black/10 transition-opacity opacity-0 group-hover:opacity-100" />
                <div className="opacity-0 group-hover:opacity-100 absolute -bottom-16 left-1/2 -translate-x-1/2 bg-background border rounded px-2 py-1 text-xs whitespace-nowrap transition-opacity z-10">
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 rotate-45 bg-background border-t border-l" />
                  <p className="font-medium">{item.category}</p>
                  <p>
                    ${item.spent.toLocaleString()} / $
                    {item.budget.toLocaleString()}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex h-8 w-full overflow-hidden rounded">
          {data.map((item) => {
            const widthPercentage = (item.budget / totalBudget) * 100;
            const fillPercentage = Math.min(
              100,
              (item.spent / item.budget) * 100
            );

            return (
              <div
                key={item.category}
                className="h-full relative"
                style={{
                  width: `${widthPercentage}%`,
                  backgroundColor: `${item.color}40`,
                }}
              >
                <div
                  className="h-full absolute top-0 left-0"
                  style={{
                    width: `${fillPercentage}%`,
                    backgroundColor: item.color,
                  }}
                />
              </div>
            );
          })}
        </div>

        <div className="flex justify-between mt-2 text-xs text-muted-foreground">
          <span>$0</span>
          <span>${totalBudget.toLocaleString()}</span>
        </div>
      </div>
    );
  };

  const renderBudgetDetails = (items) => {
    return items.map((item) => {
      const percentSpent = Math.round((item.spent / item.budget) * 100);
      const overBudget = isOverBudget(item.spent, item.budget);
      const overBudgetAmount = overBudget ? item.spent - item.budget : 0;

      return (
        <div
          key={item.category}
          className={cn(
            "space-y-2 p-3 rounded transition-colors",
            overBudget &&
              "bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800"
          )}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: item.color }}
              />
              <span className="text-sm font-medium">{item.category}</span>
              {overBudget && (
                <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-xs font-medium">
                    ${overBudgetAmount.toLocaleString()} over
                  </span>
                </div>
              )}
            </div>
            <span className="text-sm text-muted-foreground">
              ${item.spent.toLocaleString()} / ${item.budget.toLocaleString()}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <Progress
              value={Math.min(100, (item.spent / item.budget) * 100)}
              className="h-2"
              indicatorClassName="transition-all"
            />
            <span
              className={cn(
                "text-sm font-medium ml-2",
                overBudget
                  ? "text-red-600 dark:text-red-400"
                  : percentSpent >= 85
                  ? "text-amber-500"
                  : "text-green-500"
              )}
            >
              {percentSpent}%
            </span>
          </div>
          {/* Show sub-elements if this is the selected category */}
          {selectedCategory && selectedCategory.category === item.category && (
            <div
              className="ml-4 mt-2 space-y-2 border-l-2 pl-4"
              style={{ borderColor: item.color }}
            >
              {item.subElements.map((subItem) => {
                const subPercentSpent = Math.round(
                  (subItem.spent / subItem.budget) * 100
                );
                const subOverBudget = isOverBudget(
                  subItem.spent,
                  subItem.budget
                );
                const subOverBudgetAmount = subOverBudget
                  ? subItem.spent - subItem.budget
                  : 0;

                return (
                  <div
                    key={subItem.category}
                    className={cn(
                      "space-y-1 p-2 rounded",
                      subOverBudget &&
                        "bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800"
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: subItem.color }}
                        />
                        <span className="text-xs font-medium">
                          {subItem.category}
                        </span>
                        {subOverBudget && (
                          <div className="flex items-center gap-1 text-red-600 dark:text-red-400">
                            <AlertTriangle className="w-3 h-3" />
                            <span className="text-xs font-medium">
                              ${subOverBudgetAmount.toLocaleString()} over
                            </span>
                          </div>
                        )}
                      </div>
                      <span className="text-xs text-muted-foreground">
                        ${subItem.spent.toLocaleString()} / $
                        {subItem.budget.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <Progress
                        value={Math.min(
                          100,
                          (subItem.spent / subItem.budget) * 100
                        )}
                        className="h-1.5"
                        indicatorClassName="transition-all"
                      />
                      <span
                        className={cn(
                          "text-xs font-medium ml-2",
                          subOverBudget
                            ? "text-red-600 dark:text-red-400"
                            : subPercentSpent >= 85
                            ? "text-amber-500"
                            : "text-green-500"
                        )}
                      >
                        {subPercentSpent}%
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    });
  };

  const getCriticalityColor = (criticality) => {
    switch (criticality.toLowerCase()) {
      case "high":
        return "bg-red-100 text-red-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "low":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "approved":
        return "bg-green-100 text-green-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const renderIPRTable = (iprs) => {
    return (
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Initial Purchase Requests</CardTitle>
          <CardDescription>
            Detailed list of IPRs for the selected category
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>ID</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Criticality</TableHead>
                  <TableHead>Target Date</TableHead>
                  <TableHead>Budget</TableHead>
                  <TableHead>Spent</TableHead>
                  <TableHead>Created By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {iprs.map((ipr) => (
                  <TableRow
                    key={ipr.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => {
                      setSelectedData(ipr);
                      setShowModal(true);
                    }}
                  >
                    <TableCell className="font-medium">{ipr.id}</TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{ipr.title}</div>
                        <div className="text-sm text-muted-foreground">
                          {ipr.ai_summary}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(ipr.status)}>
                        {ipr.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={getCriticalityColor(ipr.criticality)}>
                        {ipr.criticality}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(ipr.delivery_target_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell>${ipr.budget.toLocaleString()}</TableCell>
                    <TableCell>${ipr.amount_spent.toLocaleString()}</TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{ipr.created_by_name}</div>
                        <div className="text-sm text-muted-foreground">
                          {ipr.source_email}
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader>
          <CardTitle>
            {selectedCategory
              ? `${selectedCategory.category} Breakdown`
              : "Budget Overview"}
          </CardTitle>
          <CardDescription>
            {selectedCategory ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setSelectedCategory(null)}
                  className="text-sm text-blue-500 hover:underline"
                >
                  ← Back to Overview
                </button>
                <span>
                  Total: ${selectedCategory.spent.toLocaleString()} of $
                  {selectedCategory.budget.toLocaleString()}
                </span>
              </div>
            ) : (
              `Total spent: $${totalSpent.toLocaleString()}`
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {renderBudgetGraph(
            selectedCategory ? selectedCategory.subElements : budgetData
          )}

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {selectedCategory
              ? // Show only sub-elements when a category is selected
                selectedCategory.subElements.map((item) => {
                  const percentSpent = Math.round(
                    (item.spent / item.budget) * 100
                  );
                  const isOverBudget = item.spent > item.budget;

                  return (
                    <div
                      key={item.category}
                      className="space-y-2 cursor-pointer hover:bg-muted/50 p-2 rounded"
                      onClick={() => setSelectedSubCategory(item)}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: item.color }}
                        />
                        <span className="text-sm font-medium">
                          {item.category}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">
                          ${item.spent.toLocaleString()} / $
                          {item.budget.toLocaleString()}
                        </span>
                        <span
                          className={`text-sm font-medium ${
                            isOverBudget
                              ? "text-red-500"
                              : percentSpent >= 85
                              ? "text-amber-500"
                              : "text-green-500"
                          }`}
                        >
                          {percentSpent}%
                        </span>
                      </div>
                    </div>
                  );
                })
              : // Show main categories when no category is selected
                renderBudgetDetails(budgetData)}
          </div>
        </CardContent>
      </Card>

      {selectedCategory && (
        <Card>
          <CardHeader>
            <CardTitle>
              {selectedCategory
                ? `${selectedCategory.category} Budget Details`
                : "Budget Details"}
            </CardTitle>
            <CardDescription>
              {selectedCategory
                ? "Detailed breakdown of sub-categories"
                : "Detailed breakdown by category"}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {selectedCategory
                ? // Show only sub-elements when a category is selected
                  selectedCategory.subElements.map((item) => (
                    <div
                      key={item.category}
                      className="space-y-2 cursor-pointer hover:bg-muted/50 p-2 rounded"
                      onClick={() => setSelectedSubCategory(item)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: item.color }}
                          />
                          <span className="text-sm font-medium">
                            {item.category}
                          </span>
                        </div>
                        <span className="text-sm text-muted-foreground">
                          ${item.spent.toLocaleString()} / $
                          {item.budget.toLocaleString()}
                        </span>
                      </div>
                      <Progress
                        value={(item.spent / item.budget) * 100}
                        className="h-3"
                        indicatorClassName="transition-all"
                        style={{
                          "--progress-background": item.color,
                          "--progress-foreground": `${item.color}20`,
                        }}
                      />
                    </div>
                  ))
                : // Show main categories when no category is selected
                  budgetData.map((item) => (
                    <div
                      key={item.category}
                      className="space-y-2 cursor-pointer hover:bg-muted/50 p-2 rounded"
                      onClick={() => setSelectedCategory(item)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: item.color }}
                          />
                          <span className="text-sm font-medium">
                            {item.category}
                          </span>
                        </div>
                        <span className="text-sm text-muted-foreground">
                          ${item.spent.toLocaleString()} / $
                          {item.budget.toLocaleString()}
                        </span>
                      </div>
                      <Progress
                        value={(item.spent / item.budget) * 100}
                        className="h-3"
                        indicatorClassName="transition-all"
                        style={{
                          "--progress-background": item.color,
                          "--progress-foreground": `${item.color}20`,
                        }}
                      />
                    </div>
                  ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Add IPR section when a subcategory is selected */}
      {selectedCategory &&
        selectedSubCategory &&
        renderIPRTable(selectedSubCategory.iprs)}

      {showModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 overflow-hidden">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowModal(false)}
          />
          <div className="relative z-[101] w-full max-w-[90vw] mx-auto max-h-[90vh] flex flex-col bg-white dark:bg-slate-900 c shadow-xl">
            {/* Contenedor con scroll interno */}
            <div className="flex-1 overflow-y-auto">
              <FlowModalFMM
                data={{
                  ...selectedData,
                  client_id: mantisBusiness._id,
                  workflow_id: 1,
                  order_id: selectedData?.id || selectedData?._id,
                }}
                onClose={() => setShowModal(false)}
                columnLabels={columnLabels}
                formatCellValue={formatCellValue}
                isOpen={showModal}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default connect(selectAuthState)(BudgetDashboard);
